import { ChevronUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Badge, Button, Group, NumberInput, Paper, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { memo, useCallback, useRef } from 'react';
import { CertRow, LangRow, SkillRequirementSelector, TechRow } from './SkillRequirementSelector';

export interface PositionFormRow {
    title: string;
    description: string;
    headCount: number;
    sortOrder: number;
    technologies: TechRow[];
    certifications: CertRow[];
    languages: LangRow[];
}

interface Props {
    position: PositionFormRow;
    index: number;
    onChange: (v: PositionFormRow) => void;
    onRemove: () => void;
    canRemove: boolean;
    showErrors?: boolean;
    onCollapse?: () => void;
}

export const PositionEditor = memo(({ position, index, onChange, onRemove, canRemove, showErrors, onCollapse }: Props) => {
    // Keep a ref to always call onChange with the latest position
    const positionRef = useRef(position);
    positionRef.current = position;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const set = <K extends keyof PositionFormRow>(key: K, value: PositionFormRow[K]) =>
        onChangeRef.current({ ...positionRef.current, [key]: value });

    // Stable callbacks so SkillRequirementSelector doesn't re-render when text fields change
    const handleTechChange = useCallback((v: TechRow[]) => {
        onChangeRef.current({ ...positionRef.current, technologies: v });
    }, []);
    const handleCertChange = useCallback((v: CertRow[]) => {
        onChangeRef.current({ ...positionRef.current, certifications: v });
    }, []);
    const handleLangChange = useCallback((v: LangRow[]) => {
        onChangeRef.current({ ...positionRef.current, languages: v });
    }, []);

    const displayTitle = position.title.trim() || 'New Position';

    return (
        <Paper withBorder p="md" radius="md" style={{ borderLeft: '3px solid var(--mantine-color-brand-5)' }}>
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group
                        gap="sm"
                        onClick={onCollapse}
                        style={{ cursor: onCollapse ? 'pointer' : undefined, flex: 1, minWidth: 0 }}
                    >
                        <Badge variant="filled" color="brand.5" size="md" circle style={{ flexShrink: 0 }}>
                            {index + 1}
                        </Badge>
                        <Text fw={600} size="sm" lineClamp={1}>{displayTitle}</Text>
                        {position.headCount > 1 && (
                            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>× {position.headCount}</Text>
                        )}
                    </Group>
                    <Group gap={4} style={{ flexShrink: 0 }}>
                        {canRemove && (
                            <Button
                                variant="subtle"
                                color="red"
                                size="xs"
                                leftSection={<TrashIcon style={{ width: 13, height: 13 }} />}
                                onClick={onRemove}
                            >
                                Remove
                            </Button>
                        )}
                        {onCollapse && (
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="sm"
                                onClick={onCollapse}
                                aria-label="Collapse position"
                            >
                                <ChevronUpIcon style={{ width: 14, height: 14 }} />
                            </ActionIcon>
                        )}
                    </Group>
                </Group>

                <Group align="flex-start">
                    <TextInput
                        label="Position Title"
                        placeholder="e.g. Senior Java Developer"
                        required
                        style={{ flex: 3 }}
                        value={position.title}
                        onChange={(e) => set('title', e.currentTarget.value)}
                        error={showErrors && !position.title.trim() ? 'Required' : undefined}
                    />
                    <NumberInput
                        label="Head Count"
                        min={1}
                        max={99}
                        style={{ flex: 1 }}
                        value={position.headCount}
                        onChange={(v) => set('headCount', typeof v === 'number' ? v : 1)}
                    />
                </Group>

                <Textarea
                    label="Description"
                    placeholder="Describe this specific position…"
                    minRows={2}
                    autosize
                    value={position.description}
                    onChange={(e) => set('description', e.currentTarget.value)}
                />

                <SkillRequirementSelector
                    technologies={position.technologies}
                    onTechnologiesChange={handleTechChange}
                    certifications={position.certifications}
                    onCertificationsChange={handleCertChange}
                    languages={position.languages}
                    onLanguagesChange={handleLangChange}
                />
            </Stack>
        </Paper>
    );
});
